import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function Profile() {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState({ enabled: false, day_of_week: 0 });
  const [recap, setRecap] = useState({ enabled: true });
  const [lastReorder, setLastReorder] = useState(null);
  const [saving, setSaving] = useState(false);
  const [recapSaving, setRecapSaving] = useState(false);
  const [prefsLoaded, setPrefsLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/me/preferences");
        setPrefs(data.weekly_reorder || { enabled: false, day_of_week: 0 });
        setRecap(data.weekly_recap || { enabled: true });
        setLastReorder(data.last_auto_reorder_at);
      } catch {
        // best-effort load — keep defaults
      } finally {
        setPrefsLoaded(true);
      }
    })();
  }, []);

  const save = async (next) => {
    setPrefs(next);
    setSaving(true);
    try {
      await api.put("/me/preferences", next);
      toast.success(next.enabled ? `Weekly reorder set for ${DAYS[next.day_of_week]}s` : "Weekly reorder off");
    } catch {
      toast.error("Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  const saveRecap = async (next) => {
    setRecap(next);
    setRecapSaving(true);
    try {
      await api.put("/me/preferences/recap", next);
      toast.success(next.enabled ? "Sunday recap email on" : "Sunday recap email off");
    } catch {
      toast.error("Couldn't save");
    } finally {
      setRecapSaving(false);
    }
  };

  if (!user) return null;
  return (
    <div className="space-y-8 ff-fade-in max-w-2xl">
      <header>
        <div className="ff-label">Profile</div>
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight mt-2" style={{ fontFamily: "Outfit, sans-serif" }}>
          {user.name || "You"}
        </h1>
      </header>

      <div className="ff-card p-6 space-y-4">
        <Row label="Email" value={user.email} />
        <Row label="Sign-in" value={user.auth_provider === "google" ? "Google" : "Email & password"} />
      </div>

      <ManageSubscriptionCard />

      {/* Weekly reorder card */}
      <div className="ff-card p-6 md:p-8" data-testid="weekly-reorder-card">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-[#3B5A3E] text-white flex items-center justify-center text-2xl flex-none">🔁</div>
          <div className="flex-1 min-w-0">
            <div className="ff-label">Auto-pilot</div>
            <h3 className="text-xl font-medium mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
              Schedule weekly reorder
            </h3>
            <p className="text-sm text-[#5C6B61] mt-2">
              We&apos;ll automatically clone your last basket every week so you can re-shop in one tap. Runs lazily — happens the next time you open Shop, Cal & Meal on the chosen day.
            </p>

            <div className="mt-5 flex items-center justify-between gap-3 flex-wrap">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={prefs.enabled}
                  onChange={(e) => save({ ...prefs, enabled: e.target.checked })}
                  disabled={saving}
                  className="w-5 h-5 rounded accent-[#3B5A3E]"
                  data-testid="weekly-reorder-toggle"
                />
                <span className="text-sm font-medium">{prefs.enabled ? "On" : "Off"}</span>
              </label>

              <div className="flex items-center gap-2">
                <span className="text-xs text-[#8B9690]">Day:</span>
                <select
                  value={prefs.day_of_week}
                  onChange={(e) => save({ ...prefs, day_of_week: Number(e.target.value) })}
                  disabled={!prefs.enabled || saving}
                  className="ff-input text-sm py-1.5 px-3 disabled:opacity-50"
                  data-testid="weekly-reorder-day"
                  style={{ width: "auto" }}
                >
                  {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
                </select>
              </div>
            </div>

            {lastReorder && (
              <p className="text-xs text-[#8B9690] mt-4">
                Last auto-reorder: {new Date(lastReorder).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Weekly recap email card */}
      <div className="ff-card p-6 md:p-8" data-testid="weekly-recap-card">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-[#C36952] text-white flex items-center justify-center text-2xl flex-none">📬</div>
          <div className="flex-1 min-w-0">
            <div className="ff-label">Sunday recap</div>
            <h3 className="text-xl font-medium mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
              Weekly recap email
            </h3>
            <p className="text-sm text-[#5C6B61] mt-2">
              Every Sunday we&apos;ll email you a one-glance summary: meals logged, calorie target hits, top recipes, and what to plan next.
              Only sent if you logged at least one thing in the week.
            </p>

            <label className="mt-5 flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={recap.enabled}
                onChange={(e) => saveRecap({ enabled: e.target.checked })}
                disabled={recapSaving || !prefsLoaded}
                className="w-5 h-5 rounded accent-[#C36952]"
                data-testid="weekly-recap-toggle"
              />
              <span className="text-sm font-medium">{recap.enabled ? "On — every Sunday evening" : "Off"}</span>
            </label>
          </div>
        </div>
      </div>

      {/* Extension card */}
      <ExtensionCard />

      {/* Mobile card */}
      <div className="ff-card p-6 md:p-8" data-testid="mobile-card">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-[#C36952] text-white flex items-center justify-center text-2xl flex-none">📱</div>
          <div className="flex-1 min-w-0">
            <div className="ff-label">iPhone & Android</div>
            <h3 className="text-xl font-medium mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
              Mobile shell ready
            </h3>
            <p className="text-sm text-[#5C6B61] mt-2">
              Shop, Cal & Meal ships with a Capacitor configuration so you can build a real iPhone or Android app from the same codebase.
              See <span className="font-mono text-[#2A332C]">/app/mobile/README.md</span> for the build steps — runs in Xcode / Android Studio.
            </p>
          </div>
        </div>
      </div>

      <ContactCard />
      <DangerZoneCard />
      <LegalFooter />
    </div>
  );
}

function ContactCard() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async () => {
    if (!subject.trim() || message.trim().length < 10) {
      toast.error("Add a subject and a message of at least 10 characters");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", { subject: subject.trim(), message: message.trim() });
      setSent(true);
      setSubject("");
      setMessage("");
      toast.success("Message sent — we'll reply to your email soon");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send message");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="ff-card p-6 md:p-8" data-testid="contact-card">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-2xl bg-[#3B5A3E] text-white flex items-center justify-center text-2xl flex-none">💬</div>
        <div className="flex-1 min-w-0">
          <div className="ff-label">Need a hand?</div>
          <h3 className="text-xl font-medium mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
            Get in touch
          </h3>
          <p className="text-sm text-[#5C6B61] mt-2">
            Found a bug, got a feature request, or just want to say hi? Drop us a line and a real human will reply.
          </p>

          {!open ? (
            <button
              onClick={() => setOpen(true)}
              data-testid="open-contact-btn"
              className="ff-btn-primary text-sm mt-4"
            >
              💬 Send us a message
            </button>
          ) : (
            <div className="mt-4 space-y-3">
              <p className="text-xs text-[#5C6B61]">
                We&apos;ll reply to <strong className="text-[#2A332C]">{user?.email}</strong>
              </p>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Subject"
                maxLength={120}
                className="ff-input text-sm w-full"
                data-testid="contact-subject-input"
              />
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="What's on your mind? Be as detailed as you'd like — screenshots are very welcome by reply email."
                rows={5}
                maxLength={4000}
                className="ff-input text-sm w-full resize-y"
                data-testid="contact-message-input"
              />
              <div className="flex items-center gap-3 flex-wrap">
                <button
                  onClick={submit}
                  disabled={sending}
                  data-testid="contact-send-btn"
                  className="ff-btn-primary text-sm disabled:opacity-60"
                >
                  {sending ? "Sending…" : "Send message"}
                </button>
                <button
                  onClick={() => { setOpen(false); setSent(false); }}
                  className="text-xs text-[#8B9690] hover:text-[#A65541]"
                  data-testid="contact-cancel-btn"
                >
                  Cancel
                </button>
                {sent && <span className="text-xs text-[#3B5A3E]" data-testid="contact-sent-pill">✓ Sent — we&apos;ll be in touch shortly</span>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-[#E5E2DA] last:border-0">
      <div className="ff-label">{label}</div>
      <div className="text-sm font-medium">{value}</div>
    </div>
  );
}

function ExtensionCard() {
  const [code, setCode] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [remaining, setRemaining] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!expiresAt) return;
    const tick = () => {
      const left = Math.max(0, Math.floor((new Date(expiresAt) - new Date()) / 1000));
      setRemaining(left);
      if (left === 0) { setCode(null); setExpiresAt(null); }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/extension/pair/start");
      setCode(data.code);
      setExpiresAt(data.expires_at);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate code");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!code) return;
    try { await navigator.clipboard.writeText(code); toast.success("Copied to clipboard"); }
    catch { toast.error("Copy failed — select the code manually"); }
  };

  const mm = String(Math.floor(remaining / 60)).padStart(1, "0");
  const ss = String(remaining % 60).padStart(2, "0");

  return (
    <div className="ff-card p-6 md:p-8" data-testid="extension-card">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-2xl bg-[#3B5A3E] text-white flex items-center justify-center text-2xl flex-none">🥗</div>
        <div className="flex-1 min-w-0">
          <div className="ff-label">Browser extension</div>
          <h3 className="text-xl font-medium mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
            Save recipes & send your basket to your supermarket
          </h3>
          <p className="text-sm text-[#5C6B61] mt-2">
            Pin the Shop, Cal & Meal extension in Chrome, Edge, Brave, or Firefox. One click saves any online recipe.
            Another click opens your weekly basket at <strong>Tesco, Sainsbury&apos;s, ASDA, Morrisons, Waitrose, Aldi or Lidl</strong> — item by item, you just tap <strong>Add</strong>.
          </p>

          {/* Pairing code generator */}
          <div className="mt-5 rounded-2xl bg-[#F3F0EA] p-4 md:p-5" data-testid="extension-pair-box">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="ff-label">Pairing code</div>
                <p className="text-xs text-[#5C6B61] mt-1">Generate a one-time 6-digit code, then enter it in the extension popup. No password typing needed.</p>
              </div>
              {!code ? (
                <button
                  onClick={generate}
                  disabled={busy}
                  className="ff-btn-primary text-sm disabled:opacity-60"
                  data-testid="extension-generate-code-btn"
                >
                  {busy ? "Generating…" : "Generate code"}
                </button>
              ) : (
                <button
                  onClick={generate}
                  disabled={busy}
                  className="text-xs text-[#3B5A3E] hover:underline"
                  data-testid="extension-regenerate-code-btn"
                >
                  Regenerate
                </button>
              )}
            </div>

            {code && (
              <div className="mt-4 flex items-center gap-4 flex-wrap" data-testid="extension-code-display">
                <button
                  onClick={copy}
                  className="font-mono text-3xl tracking-[0.4em] bg-white rounded-xl px-5 py-3 border border-[#E5E2DA] hover:bg-[#FAF9F6] transition"
                  title="Copy"
                >
                  {code}
                </button>
                <div className="text-xs text-[#5C6B61]">
                  Expires in <strong className="text-[#2A332C]">{mm}:{ss}</strong>
                  <br />Single use · auto-rotates.
                </div>
              </div>
            )}
          </div>

          <ol className="text-sm text-[#5C6B61] mt-5 space-y-1 list-decimal list-inside">
            <li><a href={`${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "")}/api/extension/download`} className="text-[#3B5A3E] font-medium hover:underline" download data-testid="profile-download-extension">Download the extension (.zip)</a> & unzip it</li>
            <li>Open <span className="font-mono text-[#2A332C]">chrome://extensions</span> (or <span className="font-mono text-[#2A332C]">about:debugging</span> in Firefox)</li>
            <li>Toggle <strong>Developer mode</strong> on (Chrome) → <strong>Load unpacked</strong> → pick the unzipped folder</li>
            <li>Click the 🥗 icon → paste the code above</li>
          </ol>

          <a
            href="/extension#mobile"
            target="_blank"
            rel="noreferrer"
            className="mt-4 inline-flex items-center gap-1.5 text-xs font-medium text-[#3B5A3E] hover:underline"
            data-testid="profile-extension-mobile-link"
          >
            📱 On your phone? See Kiwi (Android) & in-app (iPhone) guides →
          </a>
        </div>
      </div>
    </div>
  );
}

const PLAN_LABEL = { monthly: "Monthly", half_year: "6 months", yearly: "Yearly" };

function ManageSubscriptionCard() {
  const { refresh } = useAuth();
  const [me, setMe] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCancel, setShowCancel] = useState(false);
  const [reason, setReason] = useState("");
  const [refundReq, setRefundReq] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [m, h] = await Promise.all([
        api.get("/billing/me"),
        api.get("/billing/history"),
      ]);
      setMe(m.data);
      setHistory(h.data.transactions || []);
    } catch {
      // best-effort — keep existing state
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [m, h] = await Promise.all([
          api.get("/billing/me"),
          api.get("/billing/history"),
        ]);
        if (cancelled) return;
        setMe(m.data);
        setHistory(h.data.transactions || []);
      } catch {
        // ignore — empty defaults
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const submitCancel = async () => {
    if (confirmText.trim().toUpperCase() !== "CANCEL") {
      toast.error("Type CANCEL to confirm");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/billing/cancel", {
        reason: reason.trim() || null,
        request_refund: refundReq,
      });
      toast.success(
        data.refund_requested
          ? "Subscription cancelled — refund request sent to the team"
          : "Subscription cancelled"
      );
      setShowCancel(false);
      setReason("");
      setRefundReq(false);
      setConfirmText("");
      await refresh();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel subscription");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="ff-card p-6 md:p-8" data-testid="subscription-card-loading">
        <div className="ff-label">Subscription</div>
        <p className="text-sm text-[#5C6B61] mt-2">Loading your billing details…</p>
      </div>
    );
  }

  const expiresAt = me?.expires_at ? new Date(me.expires_at) : null;
  const daysLeft = expiresAt
    ? Math.max(0, Math.ceil((expiresAt - new Date()) / (1000 * 60 * 60 * 24)))
    : 0;
  const lastPaid = history.find((t) => t.payment_status === "paid" && t.applied);
  const planLabel = lastPaid ? (PLAN_LABEL[lastPaid.plan_id] || lastPaid.plan_id) : null;

  return (
    <div className="ff-card p-6 md:p-8 space-y-5" data-testid="subscription-card">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-2xl bg-[#3B5A3E] text-white flex items-center justify-center text-2xl flex-none">💳</div>
        <div className="flex-1 min-w-0">
          <div className="ff-label">Billing</div>
          <h3 className="text-xl font-medium mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
            Manage subscription
          </h3>

          {me?.pro ? (
            <div className="mt-3 space-y-1">
              <p className="text-sm text-[#2A332C]" data-testid="subscription-status">
                <span className="inline-block bg-[#E8F0E9] text-[#3B5A3E] text-xs font-medium px-2.5 py-1 rounded-full mr-2">
                  ✓ Pro active
                </span>
                {planLabel && <span className="text-[#5C6B61]">on the {planLabel} plan</span>}
              </p>
              {expiresAt && (
                <p className="text-sm text-[#5C6B61]" data-testid="subscription-expiry">
                  Renews <strong className="text-[#2A332C]">{expiresAt.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</strong>
                  {" "}· {daysLeft} day{daysLeft === 1 ? "" : "s"} remaining
                </p>
              )}
            </div>
          ) : (
            <div className="mt-3 space-y-2">
              <p className="text-sm text-[#5C6B61]">
                You&apos;re on the <strong className="text-[#2A332C]">Free</strong> plan. Upgrade to unlock the full planner, unlimited scans and shopping lists.
              </p>
              <Link
                to="/app/billing"
                className="ff-btn-primary text-sm inline-block"
                data-testid="upgrade-pro-link"
              >
                See Pro plans
              </Link>
            </div>
          )}
        </div>
      </div>

      {history.length > 0 ? (
        <div className="border-t border-[#E5E2DA] pt-5">
          <div className="ff-label mb-3">Payment history</div>
          <div className="overflow-x-auto -mx-2">
            <table className="w-full text-sm" data-testid="payment-history-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-[#8B9690]">
                  <th className="px-2 py-2 font-medium">Date</th>
                  <th className="px-2 py-2 font-medium">Plan</th>
                  <th className="px-2 py-2 font-medium text-right">Amount</th>
                  <th className="px-2 py-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {history.map((t) => {
                  const dt = t.created_at ? new Date(t.created_at) : null;
                  const status = t.applied
                    ? "paid"
                    : t.payment_status === "paid"
                    ? "paid"
                    : t.status === "open"
                    ? "pending"
                    : t.payment_status || t.status || "unknown";
                  const isPaid = status === "paid";
                  return (
                    <tr key={t.session_id} className="border-t border-[#F0EFE9]" data-testid="payment-history-row">
                      <td className="px-2 py-2.5 text-[#2A332C]">
                        {dt ? dt.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                      </td>
                      <td className="px-2 py-2.5 text-[#5C6B61]">
                        {PLAN_LABEL[t.plan_id] || t.plan_id}
                        {t.coupon_code && <span className="ml-1 text-xs text-[#3B5A3E]">· {t.coupon_code}</span>}
                      </td>
                      <td className="px-2 py-2.5 text-right text-[#2A332C] font-medium tabular-nums">
                        £{(t.amount_gbp ?? 0).toFixed(2)}
                      </td>
                      <td className="px-2 py-2.5">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          isPaid ? "bg-[#E8F0E9] text-[#3B5A3E]" : "bg-[#F0EFE9] text-[#8B9690]"
                        }`}>
                          {isPaid ? "Paid" : status.charAt(0).toUpperCase() + status.slice(1)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-[#8B9690] mt-3">
            Receipts are emailed automatically by Stripe to your sign-up email.
          </p>
        </div>
      ) : (
        me?.pro && (
          <div className="border-t border-[#E5E2DA] pt-5">
            <div className="ff-label mb-2">Payment history</div>
            <p className="text-sm text-[#5C6B61]" data-testid="history-empty-hint">
              No payments on file yet — your Pro access was granted directly by our team.
            </p>
          </div>
        )
      )}

      {me?.pro && (
        <div className="border-t border-[#E5E2DA] pt-5">
          {!showCancel ? (
            <button
              onClick={() => setShowCancel(true)}
              className="text-sm text-[#A65541] hover:underline"
              data-testid="cancel-subscription-btn"
            >
              Cancel subscription
            </button>
          ) : (
            <div className="space-y-3" data-testid="cancel-subscription-form">
              <div>
                <h4 className="text-sm font-medium text-[#2A332C]">Cancel your Pro subscription</h4>
                <p className="text-xs text-[#5C6B61] mt-1">
                  Your account will switch to Free immediately and you&apos;ll lose access to Pro features.
                  Plans are one-off purchases — no future charges will be made either way.
                </p>
              </div>

              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Mind sharing why? (optional — helps us improve)"
                rows={3}
                maxLength={1000}
                className="ff-input text-sm w-full resize-y"
                data-testid="cancel-reason-input"
              />

              <label className="flex items-start gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={refundReq}
                  onChange={(e) => setRefundReq(e.target.checked)}
                  className="w-4 h-4 mt-0.5 rounded accent-[#3B5A3E]"
                  data-testid="cancel-refund-checkbox"
                />
                <span className="text-sm text-[#2A332C]">
                  Also request a refund
                  <span className="block text-xs text-[#8B9690]">
                    We&apos;ll forward this to our billing team. Refunds are issued via Stripe within 5–10 working days when eligible (typically within 30 days of purchase).
                  </span>
                </span>
              </label>

              <div>
                <label className="text-xs text-[#5C6B61] block mb-1">
                  Type <strong>CANCEL</strong> to confirm
                </label>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="CANCEL"
                  className="ff-input text-sm w-full"
                  data-testid="cancel-confirm-input"
                />
              </div>

              <div className="flex items-center gap-3 flex-wrap">
                <button
                  onClick={submitCancel}
                  disabled={busy || confirmText.trim().toUpperCase() !== "CANCEL"}
                  data-testid="cancel-confirm-btn"
                  className="rounded-full px-5 py-2.5 text-sm font-medium bg-[#A65541] text-white hover:bg-[#8e4736] transition disabled:opacity-50"
                >
                  {busy ? "Cancelling…" : "Cancel subscription"}
                </button>
                <button
                  onClick={() => {
                    setShowCancel(false);
                    setReason("");
                    setRefundReq(false);
                    setConfirmText("");
                  }}
                  className="text-sm text-[#5C6B61] hover:text-[#2A332C]"
                  data-testid="cancel-back-btn"
                >
                  Keep my Pro
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function DangerZoneCard() {
  const { logout, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  if (user?.is_admin) {
    // Owner accounts can't self-delete from the app — they manage users from the admin panel.
    return null;
  }

  const canConfirm = confirmText.trim().toUpperCase() === "DELETE";

  const handleDelete = async () => {
    if (!canConfirm) return;
    setDeleting(true);
    try {
      await api.delete("/me", { data: { confirm: "DELETE" } });
      toast.success("Account deleted. We're sorry to see you go.");
      setTimeout(() => { logout(); }, 600);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete account");
      setDeleting(false);
    }
  };

  return (
    <div className="ff-card p-6 md:p-8 border border-[#E8C4B8] bg-[#FCF6F2]" data-testid="danger-zone-card">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-2xl bg-[#A33D26] text-white flex items-center justify-center text-2xl flex-none">⚠️</div>
        <div className="flex-1 min-w-0">
          <div className="ff-label" style={{ color: "#A33D26" }}>Danger zone</div>
          <h3 className="text-xl font-medium mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
            Delete my account
          </h3>
          <p className="text-sm text-[#5C6B61] mt-2">
            Permanently delete your Shop, Cal & Meal account and every piece of data attached to it &mdash; recipes,
            shopping lists, planner, calorie log, scan history and payment references. This can be undone within 24 hours by
            emailing <a href="mailto:hello@smartershop.xyz" className="underline">hello@smartershop.xyz</a>; after that it&apos;s gone for good.
          </p>

          {!open ? (
            <button
              onClick={() => setOpen(true)}
              data-testid="delete-account-open"
              className="mt-5 px-4 py-2 rounded-full text-xs font-semibold text-[#A33D26] border border-[#E8C4B8] hover:bg-[#FBE5DE] transition"
            >
              Delete my account…
            </button>
          ) : (
            <div className="mt-5 space-y-3" data-testid="delete-account-confirm">
              <label className="block text-sm text-[#2A332C]">
                Type <strong className="font-mono text-[#A33D26]">DELETE</strong> to confirm.
              </label>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="DELETE"
                data-testid="delete-account-input"
                autoComplete="off"
                className="w-full max-w-xs px-3 py-2 rounded-xl border border-[#E5E2DA] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#A33D26]/30"
              />
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={handleDelete}
                  disabled={!canConfirm || deleting}
                  data-testid="delete-account-confirm-btn"
                  className="px-4 py-2 rounded-full text-xs font-semibold text-white bg-[#A33D26] hover:bg-[#8A3320] disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  {deleting ? "Deleting…" : "Yes, delete forever"}
                </button>
                <button
                  onClick={() => { setOpen(false); setConfirmText(""); }}
                  disabled={deleting}
                  data-testid="delete-account-cancel"
                  className="px-3 py-2 rounded-full text-xs text-[#5C6B61] hover:bg-[#F3F0EA] transition"
                >
                  Keep my account
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function LegalFooter() {
  const appVersion = process.env.REACT_APP_VERSION || "1.2.4";
  return (
    <div className="pt-4 pb-2 text-center text-xs text-[#8B9690] flex items-center justify-center gap-3 flex-wrap" data-testid="legal-footer">
      <Link to="/privacy" className="hover:text-[#3B5A3E]" data-testid="legal-link-privacy">Privacy Policy</Link>
      <span>·</span>
      <Link to="/terms" className="hover:text-[#3B5A3E]" data-testid="legal-link-terms">Terms of Service</Link>
      <span>·</span>
      <span data-testid="app-version">v{appVersion}</span>
    </div>
  );
}
